<template>
    <stat-card :expandable="expandable" :expanded="expanded" @toggle:expand="$emit('toggle:expand')">
        <template #header>
            <kimsa-combobox class="ex-metric" dense solo :items="exerciseOptions" @change="exercise = $event"
                :value="exercise" placeholder-class no-object item-value="objectID" height="30" />
            <button-tooltip v-if="!isStudent" bottom :tooltip="$t('statistics.configure_exercises')">
                <template #activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" icon class="ex-cog" @click="$refs.selector.show()">
                        <v-icon size="17">mdi-cog-outline</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>
        </template>

        <div class="ex-toolbar">
            <div class="ex-legend">
                <span class="ex-leg"><span class="ex-leg-sq ex-leg-sq--weight" />{{ $t('exercises.avg_weight') }} (kg)</span>
                <span class="ex-leg"><span class="ex-leg-sq ex-leg-sq--max" />{{ $t('exercises.max_weight') }} (kg)</span>
                <span class="ex-leg"><span class="ex-leg-sq ex-leg-sq--sets" />{{ $tc('exercises.sets', 2) }}</span>
                <span v-if="hasCompare" class="ex-leg"><span class="ex-leg-line" />{{ compareTitle }}</span>
            </div>
            <group-by-selector @change="currentGroupBy = $event" :value="currentGroupBy" />
        </div>

        <line-chart :labels="labels" :datasets="datasets" :styles="chartStyles" ref="chart" />

        <exercises-selector-statistics-dialog v-if="!isStudent" :coach-id="coachId" ref="selector"
            :limit="CATEGORY.STRONG" @saved="loadExercises" />
    </stat-card>
</template>

<script>
import GroupBySelector from "@/components/statistics/GroupBySelector";
import { exerciseKey } from '@/utils/Exercises'
import LineChart from "@/components/common/LineChart";
import _groupBy from "lodash/groupBy";
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import ExercisesSelectorStatisticsDialog from "@/components/coach/ExercisesSelectorStatisticsDialog";
import ButtonTooltip from '../common/ButtonTooltip.vue';
import StatCard from './StatCard.vue';
import { expandableCardMixin } from '@/mixins/expandableCard';
import { db } from "@/utils/Firebase";
import { ExerciseIndex } from "@/utils/Algolia";

export default {
    name: "ExercisesChart",
    components: { LineChart, GroupBySelector, KimsaCombobox, ExercisesSelectorStatisticsDialog, ButtonTooltip, StatCard },
    mixins: [expandableCardMixin],
    props: {
        docs: {
            type: Array,
            default: null,
        },
        coachId: {
            type: String,
            default: ''
        },
        compareDocs: {
            type: Array,
            default: null,
        },
        compareLabel: {
            type: String,
            default: null,
        }
    },
    data() {
        return {
            currentGroupBy: 'daily',
            exerciseOptions: [],
            exercise: [],
            routines: [],
            datasets: [],
            labels: [],
            chartStyles: { position: 'relative', height: '220px' },
        }
    },
    computed: {
        isStudent(vm = this) {
            return vm.account?.accountType === 'student'
        },
        hasCompare(vm = this) {
            return !!(vm.compareDocs && vm.compareDocs.length)
        },
        compareTitle(vm = this) {
            return vm.compareLabel || vm.$t('common.team')
        },
    },
    async mounted() {
        const vm = this
        await vm.loadExercises()
        vm.setCharts()
    },
    methods: {
        async loadExercises() {
            let vm = this
            if (!vm.coachId) return;
            vm.exerciseOptions = []
            let coachRef = await db.collection('users').doc(vm.coachId).get()
            let coachExercises = coachRef.data().statistics_exercises || null

            let filter = `objectID:"${coachExercises?.join('" OR objectID:"')}"`
            // console.log('searching by:', filter)
            ExerciseIndex.search('', {
                filters: filter,
                hitsPerPage: 10,
            })
                .then(({ hits }) => {
                    // console.log('hits', hits)
                    vm.exerciseOptions = hits
                    vm.exercise = vm.exerciseOptions[0]?.objectID || null
                })
        },
        setCharts() {
            const vm = this;
            const grouped = vm.groupRoutines(vm.loadStats(vm.docs || []))
            const compareGrouped = vm.hasCompare ? vm.groupRoutines(vm.loadStats(vm.compareDocs)) : []
            const { datasets, labels } = vm.loadDatasets(grouped, compareGrouped)
            vm.datasets = datasets
            vm.labels = labels
        },
        groupRoutines(routines) {
            const vm = this;
            let currentRoutines = routines.map(routine => {
                let date = vm.$moment(routine.date)
                if (vm.currentGroupBy === 'weekly') date = date.startOf('week')
                if (vm.currentGroupBy === 'monthly') date = date.startOf('month')

                return {
                    ...routine,
                    date: date,
                    dateString: date.format('DD MMM YY').replace('.', ''),
                }
            })

            if (vm.currentGroupBy !== 'daily') {
                let grouped = _groupBy(currentRoutines, d => {
                    return d.dateString
                })

                currentRoutines = []
                for (let currentDate in grouped) {
                    if (grouped[currentDate].length === 1) {
                        currentRoutines.push(grouped[currentDate][0]);
                    } else {
                        const group = grouped[currentDate]
                        const weights = group.map(routine => routine.weight).filter(weight => weight > 0)
                        const weight = weights.length ? weights.reduce((total, value) => total + value, 0) / weights.length : 0
                        currentRoutines.push({
                            date: group[0].date,
                            dateString: group[0].dateString,
                            sets: group.reduce((total, routine) => total + routine.sets, 0),
                            weight: Number(Number(weight).toFixed(1).replace('.0', '')),
                            maxWeight: Math.max(...group.map(routine => routine.maxWeight)),
                        });
                    }
                }
            }

            return currentRoutines
        },
        loadStats(docs) {
            const vm = this;
            let routines = docs.map(doc => {
                let sets = 0
                let weight = 0
                let maxWeight = 0
                let quantity_strong = 0
                doc?.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest' && exerciseKey(exercise) === vm.exercise) {
                            exercise.config?.sets?.forEach((set, i) => {
                                if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.STRONG) {
                                    sets++
                                    const setWeight = vm.takeValueFromSet(set, 'weight')
                                    weight += setWeight
                                    maxWeight = Math.max(maxWeight, setWeight)
                                    quantity_strong++
                                }
                            })
                        }
                    })
                })

                if (quantity_strong > 0) weight /= quantity_strong
                weight = Number(Number(weight).toFixed(1).replace('.0', ''))

                return {
                    date: doc.rawDate.full,
                    sets: sets,
                    weight: weight,
                    maxWeight: maxWeight,
                }
            })
            return routines.filter(routine => routine.sets || routine.weight)
        },
        loadDatasets(groupRoutines, compareRoutines = []) {
            const vm = this
            const bg = vm.$vuetify.theme.currentTheme.bg

            const ctx = vm.$refs.chart?.$refs?.canvas?.getContext('2d')
            let gradientFill = 'rgba(51, 153, 153, 0.15)'
            if (ctx) {
                gradientFill = ctx.createLinearGradient(0, 0, 0, 220)
                gradientFill.addColorStop(0, 'rgba(51, 153, 153, 0.4)')
                gradientFill.addColorStop(1, 'rgba(51, 153, 153, 0.02)')
            }

            const labels = groupRoutines.map(routine => routine.dateString)

            const datasets = [
                {
                    label: `${vm.$t('exercises.avg_weight')} (kg)`,
                    data: groupRoutines.map((routine, i) => ({ x: labels[i], y: routine.weight })),
                    yAxisID: 'left-axis',
                    type: 'line',
                    borderColor: '#339999',
                    backgroundColor: gradientFill,
                    fill: true,
                    lineTension: 0,
                    borderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 5,
                    pointBackgroundColor: '#339999',
                    pointBorderColor: bg,
                    pointBorderWidth: 1.5,
                },
                {
                    label: `${vm.$t('exercises.max_weight')} (kg)`,
                    data: groupRoutines.map((routine, i) => ({ x: labels[i], y: routine.maxWeight })),
                    yAxisID: 'left-axis',
                    type: 'line',
                    borderColor: '#9B6DFF',
                    fill: false,
                    lineTension: 0,
                    borderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 5,
                    pointBackgroundColor: '#9B6DFF',
                    pointBorderColor: bg,
                    pointBorderWidth: 1.5,
                },
                {
                    label: vm.$tc('exercises.sets', 2),
                    data: groupRoutines.map((routine, i) => ({ x: labels[i], y: routine.sets })),
                    yAxisID: 'right-axis',
                    type: 'line',
                    borderColor: 'rgba(204, 255, 51, 0.6)',
                    borderDash: [4, 3],
                    fill: false,
                    lineTension: 0,
                    borderWidth: 1.5,
                    pointRadius: 4,
                    pointHoverRadius: 5,
                    pointBackgroundColor: '#CCFF33',
                    pointBorderColor: bg,
                    pointBorderWidth: 1.5,
                },
            ]

            if (compareRoutines.length) {
                const compareByDate = {}
                compareRoutines.forEach(routine => { compareByDate[routine.dateString] = routine.weight })
                const compareData = labels.map(label => ({
                    x: label,
                    y: compareByDate[label] != null ? compareByDate[label] : null,
                }))
                if (compareData.some(point => point.y !== null)) {
                    datasets.push({
                        label: vm.compareTitle,
                        data: compareData,
                        yAxisID: 'left-axis',
                        type: 'line',
                        borderColor: '#66CCCC',
                        borderDash: [4, 3],
                        fill: false,
                        spanGaps: true,
                        lineTension: 0,
                        borderWidth: 2,
                        pointRadius: 3,
                        pointHoverRadius: 4,
                        pointBackgroundColor: '#66CCCC',
                        pointBorderColor: bg,
                        pointBorderWidth: 1.5,
                    })
                }
            }

            return { datasets, labels }
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            let defaultValue = 0
            let value = set.data.find(data => data.short_name === `short_${attribute}`)
            return !!value || value === 0 ? Number(value.value || 0) : defaultValue
        },
    },
    watch: {
        exercise() {
            this.setCharts()
        },
        coachId() {
            this.loadExercises()
        },
        docs() {
            this.setCharts()
        },
        compareDocs() {
            this.setCharts()
        },
        currentGroupBy() {
            this.setCharts()
        }
    }
}
</script>

<style scoped>
.ex-leg-line {
    width: 16px;
    height: 0;
    border-top: 2px dashed #66CCCC;
}

.ex-metric {
    flex: 1;
    max-width: 220px;
}

.ex-metric ::v-deep .v-input,
.ex-metric ::v-deep .v-text-field {
    margin: 0;
    padding: 0;
}

.ex-metric ::v-deep .v-input__control,
.ex-metric ::v-deep .v-input__slot {
    min-height: 30px !important;
}

.ex-metric ::v-deep .v-input__slot {
    padding: 0 10px !important;
}

.ex-metric ::v-deep .v-select__selection {
    font-size: 12px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: normal;
    color: var(--v-txtSecondary-base);
}

.ex-metric ::v-deep .v-input__append-inner {
    margin-top: 0;
    align-self: center;
}

.ex-cog.v-btn {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base) !important;
    color: var(--v-txtMuted-base) !important;
}

.ex-cog.v-btn:hover {
    color: var(--v-txtPrimary-base) !important;
    border-color: var(--v-outline2-base);
}

.ex-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.ex-legend {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.ex-leg {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.ex-leg-sq {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
}

.ex-leg-sq--weight {
    background: rgba(51, 153, 153, 0.4);
    border: 1px solid rgba(51, 153, 153, 0.6);
}

.ex-leg-sq--max {
    background: #9B6DFF;
}

.ex-leg-sq--sets {
    background: #CCFF33;
}
</style>